import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Check, X, Loader2, Users, GraduationCap, ScanLine, Flame, BookOpen, CreditCard, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api, formatDate, formatPrice, roleBadgeClass } from '../lib/api';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { UserAvatar } from '../components/UserAvatar';
import { Skeleton } from '../components/ui/skeleton';

const STAT_ICONS = {
  users: Users,
  coaches: GraduationCap,
  analyses: ScanLine,
  posts: Flame,
  courses: BookOpen,
  paid_transactions: CreditCard,
  pending_coach_requests: Clock,
};

export default function Admin() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [deciding, setDeciding] = useState(null);

  useEffect(() => {
    if (!loading && user && user.role !== 'admin') navigate('/dashboard');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    (async () => {
      try {
        const [s, r, tx] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/coach-requests', { params: { status: 'pending' } }),
          api.get('/admin/transactions'),
        ]);
        setStats(s.data);
        setRequests(r.data.items);
        setTransactions(tx.data.items);
      } catch {
        setRequests([]);
        setTransactions([]);
      }
    })();
  }, [user]);

  const decide = async (request, approve) => {
    setDeciding(request.request_id);
    try {
      await api.post(`/admin/coach-requests/${request.request_id}/decide`, { approve });
      setRequests((prev) => prev.filter((r) => r.request_id !== request.request_id));
      setStats((prev) => (prev ? { ...prev, pending_coach_requests: Math.max(0, prev.pending_coach_requests - 1), coaches: approve ? prev.coaches + 1 : prev.coaches } : prev));
      toast.success(approve ? t('approve') + ' \u2713' : t('reject') + ' \u2713');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setDeciding(null);
    }
  };

  if (loading || user?.role !== 'admin') return null;

  const txStatusCls = (s) => (s === 'paid'
    ? 'bg-[rgba(34,197,94,0.14)] text-[#22C55E] border-[rgba(34,197,94,0.3)]'
    : s === 'pending'
    ? 'bg-[rgba(233,196,106,0.12)] text-[#E9C46A] border-[rgba(233,196,106,0.3)]'
    : 'bg-white/[0.07] text-white/50 border-white/10');

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="admin-page">
      <div className="pt-2">
        <h1 className="font-display font-bold text-2xl tracking-tight text-white flex items-center gap-2.5">
          <ShieldCheck size={22} className="text-white/70" /> {t('adminTitle')}
        </h1>
      </div>

      {/* stats bento */}
      <div>
        <h2 className="font-display font-bold text-lg tracking-tight text-white mb-2.5">{t('stats')}</h2>
        {stats === null ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full bg-white/[0.05] rounded-[20px]" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="admin-stats-grid">
            {Object.entries(t('adminStats')).map(([key, label], i) => {
              const Icon = STAT_ICONS[key] || Users;
              return (
                <motion.div key={key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="ss-card p-4" data-testid={`admin-stat-${key}`}>
                  <div className="flex items-center gap-1.5 text-white/40 mb-1.5">
                    <Icon size={12} />
                    <span className="font-mono text-[10px] uppercase tracking-wide truncate">{label}</span>
                  </div>
                  <div className="font-display font-bold text-2xl tabular-nums text-white">{stats[key] ?? 0}</div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* coach applications */}
      <div>
        <h2 className="font-display font-bold text-lg tracking-tight text-white mb-2.5">{t('coachRequests')}</h2>
        {requests === null ? (
          <Skeleton className="h-24 w-full bg-white/[0.05] rounded-[20px]" />
        ) : requests.length === 0 ? (
          <div className="ss-card p-8 text-center" data-testid="admin-no-requests">
            <p className="text-sm text-white/50">{t('noPending')}</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="admin-requests-list">
            {requests.map((r) => (
              <motion.div key={r.request_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-4 space-y-3" data-testid={`admin-request-${r.request_id}`}>
                <div className="flex items-center gap-3">
                  <UserAvatar src={r.user?.picture} alt={r.user?.name} size={40} rounded={14} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{r.user?.name}</div>
                    <div className="font-mono text-[10px] text-white/40 truncate">{r.user?.email} · {formatDate(r.created_at, lang)}</div>
                  </div>
                  <span className={`shrink-0 border rounded-[10px] px-1.5 py-0.5 text-[10px] font-mono uppercase ${roleBadgeClass(r.user?.role)}`}>
                    {t('roleBadge')[r.user?.role || 'player']}
                  </span>
                </div>
                <p className="text-sm text-white/75 leading-relaxed bg-white/[0.04] border border-white/[0.07] rounded-[14px] p-3" data-testid={`admin-request-bio-${r.request_id}`}>{r.bio}</p>
                {(r.specialties || r.links) && (
                  <div className="font-mono text-[11px] text-white/45 space-y-0.5">
                    {r.specialties && <div>{t('specialties')}: <span className="text-white/70">{r.specialties}</span></div>}
                    {r.links && <div>{t('linksLabel')}: <span className="text-white/70 break-all">{r.links}</span></div>}
                  </div>
                )}
                <div className="flex gap-2.5">
                  <button
                    onClick={() => decide(r, true)}
                    disabled={deciding === r.request_id}
                    data-testid={`admin-approve-${r.request_id}`}
                    className="ss-btn-primary flex-1 h-10 text-xs font-bold font-display flex items-center justify-center gap-1.5"
                  >
                    {deciding === r.request_id ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />} {t('approve')}
                  </button>
                  <button
                    onClick={() => decide(r, false)}
                    disabled={deciding === r.request_id}
                    data-testid={`admin-reject-${r.request_id}`}
                    className="flex-1 h-10 rounded-[16px] text-xs font-medium flex items-center justify-center gap-1.5 bg-[rgba(231,111,123,0.12)] text-[#E76F7B] border border-[rgba(231,111,123,0.3)] hover:bg-[rgba(231,111,123,0.18)] transition-colors"
                  >
                    <X size={14} /> {t('reject')}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* transactions */}
      <div>
        <h2 className="font-display font-bold text-lg tracking-tight text-white mb-2.5">{t('transactions')}</h2>
        {transactions === null ? (
          <Skeleton className="h-24 w-full bg-white/[0.05] rounded-[20px]" />
        ) : transactions.length === 0 ? (
          <div className="ss-card p-8 text-center" data-testid="admin-no-transactions">
            <p className="text-sm text-white/50">{t('noTransactions')}</p>
          </div>
        ) : (
          <div className="ss-card overflow-hidden" data-testid="admin-transactions-table">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/[0.07]">
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-white/40">{t('userLabel')}</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-white/40">{t('productLabel')}</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-white/40">{t('amountLabel')}</th>
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-white/40">{t('statusLabel')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.slice(0, 30).map((tx) => (
                    <tr key={tx.session_id} className="border-b border-white/[0.05] last:border-b-0" data-testid={`admin-tx-${tx.session_id}`}>
                      <td className="px-4 py-3">
                        <div className="text-xs text-white/85 truncate max-w-[140px]">{tx.user?.name || '\u2014'}</div>
                        <div className="font-mono text-[9px] text-white/35">{formatDate(tx.created_at, lang)}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-white/70 truncate max-w-[160px]">{tx.product_label || tx.product_type}</td>
                      <td className="px-4 py-3 font-mono text-xs text-white/85 tabular-nums">{formatPrice(tx.amount, tx.currency) || '\u2014'}</td>
                      <td className="px-4 py-3">
                        <span className={`border rounded-[10px] px-2 py-0.5 font-mono text-[10px] ${txStatusCls(tx.payment_status)}`}>{tx.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
